/**
 * export-audit-log-excel.ts
 *
 * Exports the Audit Log report to a formatted Excel (.xlsx) file.
 *
 * - Columns: Timestamp, Changed By, Category, Action, Entity, Change, Detail
 * - File named "Audit_Log_YYYY-MM-DD.xlsx"
 *
 * Dependencies:
 *  - exceljs (already in package.json)
 */

import ExcelJS from 'exceljs';
import type { AuditEntry } from '@/lib/audit-log-storage';

// ── Brand colours ────────────────────────────────────────────────────────────

const HEADER_BG = '1F3864'; // Dark navy
const HEADER_TEXT = 'FFFFFF'; // White
const ALT_ROW_BG = 'F5F7FA'; // Light grey alternating rows

// ── Column definitions ───────────────────────────────────────────────────────

interface ColDef {
  header: string;
  key: string;
  width: number;
  getValue: (entry: AuditEntry) => string;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatChange(entry: AuditEntry): string {
  if (!entry.oldValue && !entry.newValue) return '';
  if (entry.oldValue && entry.newValue) return `${entry.oldValue} → ${entry.newValue}`;
  if (entry.newValue) return entry.newValue;
  return entry.oldValue ?? '';
}

const COLUMNS: ColDef[] = [
  {
    header: 'Timestamp',
    key: 'timestamp',
    width: 22,
    getValue: (e) => formatTimestamp(e.timestamp),
  },
  {
    header: 'Changed By',
    key: 'changedBy',
    width: 20,
    getValue: (e) => e.changedBy,
  },
  {
    header: 'Category',
    key: 'category',
    width: 14,
    getValue: (e) => e.category,
  },
  {
    header: 'Action',
    key: 'action',
    width: 14,
    getValue: (e) => e.action,
  },
  {
    header: 'Entity',
    key: 'entity',
    width: 30,
    getValue: (e) => e.entityName,
  },
  {
    header: 'Change',
    key: 'change',
    width: 40,
    getValue: (e) => formatChange(e),
  },
  {
    header: 'Detail',
    key: 'detail',
    width: 40,
    getValue: (e) => e.detail ?? '',
  },
];

// ── Main export function ─────────────────────────────────────────────────────

export async function exportAuditLogToExcel(entries: AuditEntry[]): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Projects';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Audit Log', {
    views: [{ state: 'frozen', ySplit: 2 }],
  });

  // ── Title row ─────────────────────────────────────────────────────
  const titleRow = sheet.addRow(['Audit Log']);
  titleRow.height = 28;
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: HEADER_TEXT } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(1, 1, 1, COLUMNS.length);

  // ── Column header row ──────────────────────────────────────────────
  sheet.columns = COLUMNS.map((col) => ({
    key: col.key,
    width: col.width,
  }));

  const headerRow = sheet.addRow(COLUMNS.map((c) => c.header));
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
    };
  });

  // ── Data rows ──────────────────────────────────────────────────────
  entries.forEach((entry, idx) => {
    const values = COLUMNS.map((col) => col.getValue(entry));
    const dataRow = sheet.addRow(values);
    dataRow.height = 18;

    const isAlt = idx % 2 === 1;
    dataRow.eachCell((cell) => {
      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }
      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
    });
  });

  // ── Auto-filter on header row ──────────────────────────────────────
  sheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: COLUMNS.length },
  };

  // ── Download ───────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const fileName = `Audit_Log_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
